import { LOCALE_CODE } from './languages/localeCodes';
import type { Locales, Markets, Market } from './languages/types';
import { ISO } from './languages/countryIsoCodes';

export const defaultLocale = LOCALE_CODE.EN_GB satisfies LOCALE_CODE;

/**
 * Locales active on shared Nuxt frontends (`apps/web`, `apps/business`).
 * Wired via `layers/base` `@nuxtjs/i18n` — do not duplicate per app.
 * If not included, the locale will not show in the language picker and pages will 404.
 * Locales still show in draft/preview mode so editors can test before going live.
 */
export const activeFrontendLocales = [
  LOCALE_CODE.EN_GB,
  LOCALE_CODE.EN_AU,
  LOCALE_CODE.DE_AT,
  LOCALE_CODE.NL_BE,
  LOCALE_CODE.FR_BE,
  LOCALE_CODE.EN_CA,
  LOCALE_CODE.FR_CA,
  LOCALE_CODE.DA,
  LOCALE_CODE.FR,
  LOCALE_CODE.DE,
  LOCALE_CODE.EN_IE,
  LOCALE_CODE.IT,
  LOCALE_CODE.NO,
  LOCALE_CODE.PL,
  LOCALE_CODE.PT,
  LOCALE_CODE.ES,
  LOCALE_CODE.SV,
  LOCALE_CODE.DE_CH,
  LOCALE_CODE.FR_CH,
  LOCALE_CODE.IT_CH,
  LOCALE_CODE.NL,
  LOCALE_CODE.EN_NZ,
  LOCALE_CODE.EN_US,
  LOCALE_CODE.JA,
];

/**
 * From our list of locales, create a list of Markets.
 * A Market is a country or region that TGTG trades in. It may have one or more locales.
 * Markets are generated from the list of locale Country names, and locales with the same Country
 * are grouped together.
 */
export const markets: Markets = {
  [ISO.GBR]: {
    country: 'United Kingdom',
    countryIso: ISO.GBR,
    icon: '🇬🇧',
    currency: 'GBP',
    locales: [{
      localeCode: LOCALE_CODE.EN_GB,
      languageName: 'English',
    }]
  },
  [ISO.AUS]: {
    country: 'Australia',
    countryIso: ISO.AUS,
    icon: '🇦🇺',
    currency: 'AUD',
    locales: [{
      localeCode: LOCALE_CODE.EN_AU,
      languageName: 'English',
    }],
  },
  [ISO.AUT]: {
    country: 'Austria',
    countryIso: ISO.AUT,
    icon: '🇦🇹',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.DE_AT,
      languageName: 'Austrian',
    }],
  },
  [ISO.BEL]: {
    country: 'Belgium',
    countryIso: ISO.BEL,
    icon: '🇧🇪',
    currency: 'EUR',
    locales: [
      {
        localeCode: LOCALE_CODE.NL_BE,
        languageName: 'Flemish',
      },
      {
        localeCode: LOCALE_CODE.FR_BE,
        languageName: 'French',
      }
    ],
  },
  [ISO.CAN]: {
    country: 'Canada',
    countryIso: ISO.CAN,
    icon: '🇨🇦',
    currency: 'CAD',
    locales: [
      {
        localeCode: LOCALE_CODE.EN_CA,
        languageName: 'English',
      },
      {
        localeCode: LOCALE_CODE.FR_CA,
        languageName: 'French',
      }
    ],
  },
  [ISO.DNK]: {
    country: 'Denmark',
    countryIso: ISO.DNK,
    icon: '🇩🇰',
    currency: 'DKK',
    locales: [{
      localeCode: LOCALE_CODE.DA,
      languageName: 'Danish',
    }],
  },
  [ISO.FRA]: {
    country: 'France',
    countryIso: ISO.FRA,
    icon: '🇫🇷',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.FR,
      languageName: 'French',
    }],
  },
  [ISO.DEU]: {
    country: 'Germany',
    countryIso: ISO.DEU,
    icon: '🇩🇪',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.DE,
      languageName: 'German',
    }],
  },
  [ISO.IRL]: {
    country: 'Ireland',
    countryIso: ISO.IRL,
    icon: '🇮🇪',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.EN_IE,
      languageName: 'Irish',
    }],
  },
  [ISO.ITA]: {
    country: 'Italy',
    countryIso: ISO.ITA,
    icon: '🇮🇹',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.IT,
      languageName: 'Italian',
    }],
  },
  [ISO.JPN]: {
    country: 'Japan',
    countryIso: ISO.JPN,
    icon: '🇯🇵',
    currency: 'JPY',
    locales: [{
      localeCode: LOCALE_CODE.JA,
      languageName: '日本語 Japanese',
    }],
  },
  [ISO.NOR]: {
    country: 'Norway',
    countryIso: ISO.NOR,
    icon: '🇳🇴',
    currency: 'NOK',
    locales: [{
      localeCode: LOCALE_CODE.NO,
      languageName: 'Norwegian',
    }],
  },
  [ISO.POL]: {
    country: 'Poland',
    countryIso: ISO.POL,
    icon: '🇵🇱',
    currency: 'PLN',
    locales: [{
      localeCode: LOCALE_CODE.PL,
      languageName: 'Polish',
    }],
  },
  [ISO.PRT]: {
    country: 'Portugal',
    countryIso: ISO.PRT,
    icon: '🇵🇹',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.PT,
      languageName: 'Portuguese',
    }],
  },
  [ISO.ESP]: {
    country: 'Spain',
    countryIso: ISO.ESP,
    icon: '🇪🇸',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.ES,
      languageName: 'Spanish',
    }],
  },
  [ISO.SWE]: {
    country: 'Sweden',
    countryIso: ISO.SWE,
    icon: '🇸🇪',
    currency: 'SEK',
    locales: [{
      localeCode: LOCALE_CODE.SV,
      languageName: 'Swedish',
    }],
  },
  [ISO.CHE]: {
    country: 'Switzerland',
    countryIso: ISO.CHE,
    icon: '🇨🇭',
    currency: 'CHF',
    locales: [
      {
        localeCode: LOCALE_CODE.DE_CH,
        languageName: 'German',
      },
      {
        localeCode: LOCALE_CODE.FR_CH,
        languageName: 'French',
      },
      {
        localeCode: LOCALE_CODE.IT_CH,
        languageName: 'Italian',
      },
    ],
  },
  [ISO.NLD]: {
    country: 'The Netherlands',
    countryIso: ISO.NLD,
    icon: '🇳🇱',
    currency: 'EUR',
    locales: [{
      localeCode: LOCALE_CODE.NL,
      languageName: 'Dutch',
    }],
  },
  [ISO.NZL]: {
    country: 'New Zealand',
    countryIso: ISO.NZL,
    icon: '🇳🇿',
    currency: 'NZD',
    locales: [{
      localeCode: LOCALE_CODE.EN_NZ,
      languageName: 'English',
    }],
  },
  [ISO.USA]: {
    country: 'United States',
    countryIso: ISO.USA,
    icon: '🇺🇸',
    currency: 'USD',
    locales: [{
      localeCode: LOCALE_CODE.EN_US,
      languageName: 'English',
    }],
  },
};

/**
 * The locales available to the CMS and website.
 * They are generated from the locales in the Markets listed above.
 * A locale cannot belong to more than one market.
 * Add more locales to the list of markets above to support more languages across our application.
 */
export const locales: Locales = Object.keys(markets).reduce<Locales>((localesAcc, countryIso) => {
  const market = markets[countryIso];
  const locales = market.locales;
  const newLocales = locales.reduce((acc, locale) => ({
    ...acc,
    [locale.localeCode]: {
      country: market.country,
      countryIso: market.countryIso,
      languageName: locale.languageName,
      icon: market.icon,
      currency: market.currency,
    },
  }), {});

  return {
    ...localesAcc,
    ...newLocales,
  };
}, {} as Locales);

export const getLocaleFromLocaleCode = (localeCode: LOCALE_CODE) => {
  return locales[localeCode];
}

export const supportedLocales = Object.keys(locales) as Array<LOCALE_CODE>;

export function countrySuffixesFromLocales(market: Market) {
  const codes = market.locales.map(locale => locale.localeCode);

  // Process and simplify codes: get the suffix if a 5 character code "en-gb => gb", or whole string if not "fr => fr"
  const simplifiedCodes = codes.map(code => code.length === 5 ? code.substring(3) : code);

  const uniqueCodes = new Set(simplifiedCodes);

  return Array.from(uniqueCodes);
}
